import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { NoticeItem } from '../types';

function NoticeCarousel({ notices }: { notices: NoticeItem[] }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [opacity, setOpacity] = useState(1);
  const slideStartRef = React.useRef(Date.now());
  const advancingRef = React.useRef(false);

  useEffect(() => {
    setCurrentIndex(0);
    setProgress(0);
    setOpacity(1);
    slideStartRef.current = Date.now();
    advancingRef.current = false;
  }, [notices.length]);

  useEffect(() => {
    if (notices.length <= 1) return;
    const current = notices[currentIndex];
    const durationMs = Math.max(2000, (current?.durationSeconds ?? 5) * 1000);
    const tick = () => {
      if (advancingRef.current) return;
      const elapsed = Date.now() - slideStartRef.current;
      const p = Math.min(100, (elapsed / durationMs) * 100);
      setProgress(p);
      if (p >= 100) {
        advancingRef.current = true;
        setOpacity(0);
        setTimeout(() => {
          setCurrentIndex((i) => (i + 1) % notices.length);
          slideStartRef.current = Date.now();
          setProgress(0);
          setOpacity(1);
          advancingRef.current = false;
        }, 400);
      }
    };
    const id = setInterval(tick, 100);
    return () => clearInterval(id);
  }, [notices, currentIndex]);

  if (!notices.length) {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.05rem', textAlign: 'center' }}>No notices for now.</Typography>
      </Box>
    );
  }
  if (notices.length === 1) {
    const one = notices[0];
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center' }}>
        {one.imageDataUrl && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: one.text ? 1 : 0 }}>
            <Box component="img" src={one.imageDataUrl} alt="" sx={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain' }} />
          </Box>
        )}
        {one.text && <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>{one.text}</Typography>}
      </Box>
    );
  }
  const current = notices[currentIndex];
  return (
    <Box sx={{ textAlign: 'center', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', transition: 'opacity 0.4s ease-in-out', opacity }}>
        {current?.imageDataUrl && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: current?.text ? 1 : 0 }}>
            <Box component="img" src={current.imageDataUrl} alt="" sx={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain' }} />
          </Box>
        )}
        {current?.text && <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>{current.text}</Typography>}
      </Box>
      <Box sx={{ flexShrink: 0 }}>
        <Box sx={{ height: 4, borderRadius: 1, bgcolor: 'action.hover', overflow: 'hidden' }}>
          <Box
            sx={{
              height: '100%',
              width: `${progress}%`,
              bgcolor: 'primary.main',
              borderRadius: 1,
              transition: 'width 0.1s linear',
            }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Notice {currentIndex + 1} of {notices.length}
        </Typography>
      </Box>
    </Box>
  );
}

export default NoticeCarousel;
